import { Command } from 'common/Prose/Commands'
import { preserveSelection } from 'common/Prose/utils'
import { MarkType, Node } from 'prosemirror-model'
import { SelectionRange, TextSelection } from 'prosemirror-state'

function markApplies(doc: Node, ranges: SelectionRange[], type: MarkType) {
  for (let i = 0; i < ranges.length; i++) {
    let { $from, $to } = ranges[i]
    let can = $from.depth === 0 ? doc.type.allowsMarkType(type) : false
    doc.nodesBetween($from.pos, $to.pos, (node) => {
      if (can) return false
      can = node.inlineContent && node.type.allowsMarkType(type)
    })
    if (can) return true
  }
  return false
}

/**
 * Create a command function that toggles the given mark with the
 * given attributes. Will return `false` when the current selection
 * doesn't support that mark. This will remove the mark if any marks
 * of that type exist in the selection, or add it otherwise. If the
 * selection is empty, this applies to the [stored
 * marks](#state.EditorState.storedMarks) instead of a range of the
 * document.
 *
 * @param {MarkType} markType - the type of the mark
 * @param {any} attrs - The attrs for the mark
 * @return {(state: EditorState, dispatch?: (tr: Transaction) => void, view?: EditorView, overwriteSelectionRanges?: AutoTextSelectionRange)} command => boolean
 *
 * For chain commands, prosemirror adds a 3rd parameter to the return func which is `EditorView`.
 * check node_modules/@types/prosemirror-commands/index.d.ts L-188`
 */
export function toggleMark(markType: MarkType, attrs?: any): Command {
  return function (state, dispatch, view): boolean {
    const { empty, $cursor } = state.selection as TextSelection
    let { ranges } = state.selection as TextSelection
    if ((empty && !$cursor) || !markApplies(state.doc, ranges, markType)) return false
    if (dispatch) {
      if ($cursor) {
        if (markType.isInSet(state.storedMarks || $cursor.marks())) dispatch(state.tr.removeStoredMark(markType))
        else dispatch(state.tr.addStoredMark(markType.create(attrs)))
      } else {
        let has = false,
          tr = state.tr
        for (let i = 0; !has && i < ranges.length; i++) {
          let { $from, $to } = ranges[i]
          has = state.doc.rangeHasMark($from.pos, $to.pos, markType)
        }
        for (let i = 0; i < ranges.length; i++) {
          let { $from, $to } = ranges[i]
          if (has) {
            tr.removeMark($from.pos, $to.pos, markType)
          } else {
            let from = $from.pos,
              to = $to.pos,
              start = $from.nodeAfter,
              end = $to.nodeBefore
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            let spaceStart = start?.isText ? /^\s*/.exec(start.text!)![0].length : 0
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            let spaceEnd = end?.isText ? /\s*$/.exec(end.text!)![0].length : 0
            if (from + spaceStart < to) {
              from += spaceStart
              to -= spaceEnd
            }
            tr.addMark(from, to, markType.create(attrs))
          }
        }

        // NOTE: almanac bugfix for when selection is before block nodes -
        // prosemirror otherwise loses the selection
        preserveSelection(tr, state)

        dispatch(tr.scrollIntoView())
      }
    }
    return true
  }
}
